import React, { useEffect, useState } from 'react';
import { fetchAdminApi } from "../../../services/api";
import constants from '../../../constants/constants';
import { Table, Space, Form, Input, Tooltip, Spin, Button } from 'antd';
import { SearchOutlined, MailOutlined, CheckCircleOutlined, IssuesCloseOutlined, CloseCircleOutlined, UsergroupAddOutlined, RollbackOutlined, FileExcelOutlined } from '@ant-design/icons';
import { COOKIE, getCookie } from '../../../services/cookie';
import { useHistory } from "react-router";
import moment from "moment";

const ClosedAndForcedList = () => {
	const [tableData, setTableData] = useState([]);
	const [filterTable, setFilterTable] = useState([]);
	const [loader, setLoader] = useState(false);
	const [pagination, setPagination] = useState(constants.paginationConfig);
	const history = useHistory();
	let loginType = getCookie(COOKIE.LoginType);

	useEffect(() => {
		getClosedAndForcedList();
	}, []);

	const getClosedAndForcedList = () => {
		setLoader(true);
		fetchAdminApi(`/closed-and-forced`, "post", {}).then((res) => {
			if (res && res.code === 200 && res.data) {
				setTableData(res.data);
				setFilterTable(res.data);
			} else {
				setTableData([]);
				setFilterTable([]);
			}
			setLoader(false);
		});
	};

	const onSearch = (event) => {
		const search = event.target.value;
		const tempData = filterTable.filter((item) =>
			item?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
			item?.email?.toLowerCase()?.includes(search?.toLowerCase()) ||
			item?.contact_no?.toLowerCase()?.includes(search?.toLowerCase()) ||
			item?.status?.toLowerCase()?.includes(search?.toLowerCase()) ||
			item?.type?.toLowerCase()?.includes(search?.toLowerCase())
		);
		setTableData(tempData);
	};

	const copyToClipboard = (email) => {
		const el = document.createElement('textarea');
		el.value = email;
		document.body.appendChild(el);
		el.select();
		document.execCommand('copy');
		document.body.removeChild(el);
	};

	const routeToRecord = (record) => {
		if (record.type === "client" && record.client_id) {
			sessionStorage.removeItem("enquiryDetails");
			history.push({
				pathname: "/view-client",
				state: { client_id: record.client_id },
			});
		} else {
			sessionStorage.setItem("enquiryDetails", JSON.stringify(record));
			history.push({
				pathname: "/view-enquiry",
				state: {
					enquiry_id: record.enquiry_id,
					show_other_details: record?.show_other_details,
				},
			});
		}
	};

	const getRecordMessage = () => {
		if (tableData.length > 1) return `${tableData.length} records found.`;
		if (tableData.length === 0) return "No records found.";
		return `${tableData.length} record found.`;
	};

	const getEnquiryStatusLabel = (enquiry_status) => {
		const statusMap = {
			"0": "New",
			"1": "Parent Consent Requested",
			"2": "Parent Consent Received",
			"3": "Registration Link Sent",
			"4": "Registered (Awaiting Payment)",
			"5": "Payment Received",
			"7": "Fee Plan Sent to Caller",
			"8": "Payment Agreement Signed",
			"10": "Self Assessment Completed",
			"11": "Coaching Agreement Sent",
			"12": "Self Assessment Sent",
		};
		return statusMap[enquiry_status] || "-";
	};

	const columns = [
		{
			title: '#',
			align: "center",
			key: 'serial',
			width: "5%",
			render: (_, record) => {
				const display = record.type === "client" && record.client_serial_number
					? record.client_serial_number
					: record.enquiry_id;
				return <span className="text-right w-8 block m-auto">{display}</span>;
			},
			sorter: (a, b) => {
				const aVal = a.type === "client" ? a.client_serial_number : a.enquiry_id;
				const bVal = b.type === "client" ? b.client_serial_number : b.enquiry_id;
				return Number(aVal) - Number(bVal);
			},
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			width: "8%",
			render: (type) => <span className="capitalize">{type}</span>,
			sorter: (a, b) => (a.type || "").localeCompare(b.type || ""),
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			width: "15%",
			sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
			render: (name, record) =>
				name ? (
					<a onClick={() => routeToRecord(record)}>
						<Space>
							<SearchOutlined /> {name}
						</Space>
					</a>
				) : "",
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			width: "5%",
			align: "center",
			render: (email) => (
				<Tooltip title={email}>
					<MailOutlined onClick={() => copyToClipboard(email)} style={{ cursor: 'pointer' }} />
				</Tooltip>
			),
		},
		{
			title: 'Contact No.',
			dataIndex: 'contact_no',
			key: 'contact_no',
			width: "10%",
			render: (contact_no) =>
				contact_no && contact_no !== "00000" ? <span>{contact_no}</span> : <span>-</span>,
		},
		{
			title: 'Status',
			key: 'status',
			width: "20%",
			sorter: (a, b) => {
				const getFullStatus = (record) => {
					if (record.type === "client") {
						return record.status;
					}
					return `${record.status} (${getEnquiryStatusLabel(record.enquiry_status)})`;
				};
				return getFullStatus(a).localeCompare(getFullStatus(b));
			},
			render: (_, record) => {
				if (record.type === "client") {
					return <p>{record.status}</p>;
				}
				return (
					<p>
						{record.status + " "}
						<a>({getEnquiryStatusLabel(record.enquiry_status)})</a>
					</p>
				);
			},
		},
		{
			title: 'For',
			dataIndex: 'is_minor',
			key: 'is_minor',
			width: "5%",
			sorter: (a, b) => Number(a.is_minor) - Number(b.is_minor),
			render: (is_minor) => (
				<Tooltip title={is_minor === "0" ? "Adult" : "Minor"}>
					<span>{is_minor === "0" ? "A" : "M"}</span>
				</Tooltip>
			),
		},
		{
			title: 'Date of Enquiry',
			dataIndex: 'dt_created',
			key: 'dt_created',
			width: "10%",
			sorter: (a, b) => moment(a.dt_created || 0).unix() - moment(b.dt_created || 0).unix(),
			render: (dt_created) => (
				<span>{dt_created ? moment(dt_created).format(constants.dateformat) : "-"}</span>
			),
		},
		{
			title: 'Last Updated',
			dataIndex: 'dt_updated',
			key: 'dt_updated',
			width: "10%",
			// sorter: (a, b) => moment(a.dt_updated || 0).unix() - moment(b.dt_updated || 0).unix(),
			sorter: (a, b) => {
				if (!a.dt_updated && !b.dt_updated) return 0;
				if (!a.dt_updated) return 1;  // nulls go to bottom
				if (!b.dt_updated) return -1;
				return moment(a.dt_updated).unix() - moment(b.dt_updated).unix();
			},
			render: (dt_updated) => (
				<span>{dt_updated ? moment(dt_updated).format(constants.dateformat) : "-"}</span>
			),
		},
	];

	return (
		<div>
			<div className='text-center bg-amber-300 text-lg font-semibold p-3'>
				{constants.Subtitle}
				<span className="py-0.5 px-3 ml-3 text-sm bg-white rounded-xl">Closed & Force Closed</span>
			</div>
			<div className='container mx-auto'>
				<Space wrap className='mt-2'>
					<Space wrap>
						<Form.Item label="Search" className="mb-0">
							<Input className='w-full lg:w-52' size="middle" onChange={onSearch} />
						</Form.Item>
						<span className='font-semibold text-base'>{getRecordMessage()}</span>
					</Space>
					<Space wrap>
						{(loginType !== "3") && (
							<>
								<Button size='small' icon={<CheckCircleOutlined />} className='text-xs font-semibold bg-sky-800 text-white uppercase' onClick={() => history.push({ pathname: "/client-list-open" })}>Client Open</Button>
								<Button size='small' icon={<CheckCircleOutlined />} className='text-xs font-semibold bg-sky-800 text-white uppercase' onClick={() => history.push({ pathname: "/client-enquiry-open-list" })}>Enquiry Open</Button>
								<Button size='small' icon={<CloseCircleOutlined />} className='text-xs font-semibold bg-sky-800 text-white uppercase' onClick={() => history.push({ pathname: "/client-list-closed" })}>Client Closed</Button>
								<Button size='small' icon={<IssuesCloseOutlined />} className='text-xs font-semibold bg-sky-800 text-white uppercase' onClick={() => history.push({ pathname: "/client-enquiry-forceclosed-list" })}>Enquiry Force Closed</Button>
								<Button size='small' icon={<UsergroupAddOutlined />} className='text-xs font-semibold bg-sky-800 text-white uppercase' onClick={() => history.push({ pathname: "/group-progress-notes" })}>{constants.GroupProgressNotes}</Button>
								<Button size='small' icon={<RollbackOutlined />} className='text-xs font-semibold bg-sky-800 text-white uppercase' onClick={() => history.push({ pathname: "/dashboard-info" })}>{constants.BackToDashboard}</Button>
							</>
						)}
						<Tooltip title={constants.ExportToWord}><Button style={{ visibility: "hidden" }} className="float-right bg-sky-800 text-white" icon={<FileExcelOutlined />}>{constants.ExportToWord}</Button></Tooltip>

					</Space>
				</Space>
				<Spin spinning={loader} size="large">
					<Table
						rowKey="enquiry_id"
						pagination={pagination}
						dataSource={tableData}
						columns={columns}
						bordered
						scroll={{ x: 900 }}
						locale={{ emptyText: 'No Records Found' }}
						onChange={(paginationConfig) => setPagination(paginationConfig)}
						className='mt-2'
						size="small"
					/>
				</Spin>
			</div>
		</div>
	);
};

export default ClosedAndForcedList;